import { getClientSource } from '../utils/getClientSource';
import { BASE_URL } from './baseURL';

// API endpoints
const ENDPOINTS = {
  CHECK_PARTY: '/customer/checkparty',
  TRAVEL_QUOTE: '/travel/quote',
  BNI_TRAVEL_QUOTE: '/travel/new-quote',
  TRAVEL_PLAN_PRICES: '/travel/plan-prices',
  BNI_TRAVEL_PLAN_PRICES: '/travel/new-plan-prices',
  TRAVEL_COVER_PRICES: '/travel/cover-prices',
  BNI_TRAVEL_COVER_PRICES: '/travel/new-cover-prices',
  TRAVEL_MEMBERS: '/travel/members',
  TRAVEL_UPDATE_MEMBERS: '/travel/update-members',
  TRAVEL_DELETE_MEMBER: '/travel/delete-member',
  HOME_QUOTE: '/home/quote',
  HOME_COVER_PRICES: '/home/cover-prices',
  CYBER_PERSONAL_QUOTE: '/cyberpersonal/quote',
  SMART_DEVICE_QUOTE: '/smartdevice/quote',
  APPLY_PROMO: '/policy/applypromo'
};

// Utility functions for data transformation
export const convertToUnixTimestamp = (dateInput) => {
  console.log("🔍 convertToUnixTimestamp called with:", dateInput);
  console.log("🔍 dateInput type:", typeof dateInput);
  
  if (!dateInput) {
    console.log("❌ convertToUnixTimestamp: No dateInput provided");
    return null;
  }
  
  // Handle date object with fullDate property (travel passenger structure)
  if (typeof dateInput === 'object' && dateInput.fullDate) {
    console.log("🔄 Converting object with fullDate:", dateInput.fullDate);
    const timestamp = Math.floor(new Date(dateInput.fullDate).getTime() / 1000);
    console.log("✅ Converted to unix timestamp:", timestamp);
    return timestamp;
  }
  
  // Handle regular date string
  console.log("🔄 Converting regular date string:", dateInput);
  const timestamp = Math.floor(new Date(dateInput).getTime() / 1000);
  console.log("✅ Converted to unix timestamp:", timestamp);
  return timestamp;
};

export const getCurrentUnixTimestamp = () => {
  return Math.floor(Date.now() / 1000);
};

// Generic API call function with error handling
// Travel quote endpoint doesn't require authentication
const makeAPICall = async (endpoint, data, options = {}) => {
  const { timeout = 30000 } = options; // Increase timeout to 30 seconds

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    console.log(`=== Making API call ===`);
    console.log(`Endpoint: ${BASE_URL}${endpoint}`);
    console.log(`Payload:`, JSON.stringify(data, null, 2));
    console.log(`Timeout: ${timeout}ms`);

    // Prepare headers (no auth required for travel quote)
    const headers = {
      'Content-Type': 'application/json',
    };

    const startTime = Date.now();
    const response = await fetch(`${BASE_URL}${endpoint}`, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify(data),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);
    const endTime = Date.now();
    console.log(`API call completed in ${endTime - startTime}ms`);
    console.log(`Response status: ${response.status}`);

    if (!response.ok) {
      console.error(`API Error Response:`, response.status, response.statusText);
      // Try to get error details from response
      let errorMessage = `API Error: ${response.status} ${response.statusText}`;
      try {
        const errorData = await response.json();
        console.error(`Error data:`, errorData);
        if (errorData.ErrorMessageEn) {
          errorMessage = errorData.ErrorMessageEn;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        }
      } catch (e) {
        // Use default error message if can't parse response
      }
      throw new Error(errorMessage);
    }

    const result = await response.json();
    console.log(`=== API Success Response ===`);
    console.log(`TotalPremium: ${result.TotalPremium}`);
    console.log(`Premium: ${result.Premium}`);
    console.log(`Tax: ${result.Tax}`);
    console.log(`PlanName: ${result.PlanName}`);
    console.log(`PolicyNumber: ${result.PolicyNumber}`);

    return { success: true, data: result };

  } catch (error) {
    clearTimeout(timeoutId);

    console.error(`=== API Error ===`);
    console.error(`Error name: ${error.name}`);
    console.error(`Error message: ${error.message}`);
    console.error(`Full error:`, error);

    if (error.name === 'AbortError') {
      console.error('Request was aborted due to timeout');
      return {
        success: false,
        error: `Request timed out after ${timeout}ms`,
        isTimeout: true
      };
    }

    // Determine error type
    const isNetworkError = !navigator.onLine || error.name === 'TypeError' || error.message.includes('fetch');
    const isServerError = error.message.includes('server issues') || error.message.includes('500');

    return {
      success: false,
      error: error.message || 'API call failed',
      isNetworkError: isNetworkError,
      isServerError: isServerError,
      isTimeout: error.name === 'AbortError'
    };
  }
};

/**
 * Check user's party code using CPR
 * @param {string} cpr - User's CPR number
 * @returns {Promise<Object>} Party code response
 */
export const checkPartyCode = async (cpr) => {
  if (!cpr || !cpr.trim()) {
    return {
      success: false,
      error: 'CPR is required',
      data: { PartyCode: 'FALCON', PartyExists: false }
    };
  }

  try {
    const result = await makeAPICall(ENDPOINTS.CHECK_PARTY, { cpr: cpr.trim() }, {
      timeout: 15000
    });

    if (result.success) {
      return {
        success: true,
        data: {
          PartyCode: result.data.PartyCode || 'FALCON',
          PartyExists: result.data.PartyExists || false,
          Name: result.data.Name || '',
          DOB: result.data.DOB || ''
        }
      };
    }

    // If API fails, fallback to FALCON for guests
    console.warn('Check party API failed, using fallback:', result.error);
    return {
      success: true,
      data: { PartyCode: 'FALCON', PartyExists: false },
      fallback: true,
      error: result.error
    };

  } catch (error) {
    console.error('Check party code failed:', error);
    return {
      success: true, // Don't block user flow
      data: { PartyCode: 'FALCON', PartyExists: false },
      fallback: true,
      error: error.message
    };
  }
};

/**
 * Transform travel form data to API request format
 * @param {Object} formData - Travel form data
 * @param {string} partyCode - User's party code
 * @param {Object} options - Additional options
 * @returns {Object} API request payload
 */
const transformTravelFormData = (formData, partyCode = 'FALCON', options = {}) => {
  const {
    selectedPlan,
    departureDate,
    returnDate,
    passengers = [],
    addOns = {},
    planType: explicitPlanType, // Can be set from date selection (individual/family)
    dateOfBirth, // DateOfBirth from profile for individual plans (ISO format)
    isAnnual = false,
    isGuest = false,        // Guest (not logged-in) flag
    guestInfo = null        // { cpr, dateOfBirth, email, phoneNumber } from start-page basic info
  } = formData;

  const { includePassengers = false } = options;

  // Map plan codes to match API expectations exactly from documentation
  const planCodeMapping = {
    'silver': 'silver',    // Documentation shows 'silver' for Silver
    'platinum': 'PL',      // Documentation shows 'PL' for Platinum  
    'gold': 'GO'           // Documentation shows 'GO' for Gold
  };

  const planCode = planCodeMapping[selectedPlan?.toLowerCase()] || 'silver';
  console.log(`Plan mapping: ${selectedPlan} -> ${planCode}`);

  // Determine plan type:
  // 1. Use explicit planType from date selection if available
  // 2. Fall back to passenger count (only if we're including passengers)
  // 3. Default to 'individual' for plan selection phase
  let planType;
  if (explicitPlanType) {
    planType = explicitPlanType.toLowerCase(); // Normalize to lowercase
  } else if (includePassengers && passengers.length > 1) {
    planType = 'family';
  } else {
    planType = 'individual'; // Default for plan selection
  }

  const payload = {
    PlanCode: planCode,
    DepartureDate: convertToUnixTimestamp(departureDate),
    ReturnDate: convertToUnixTimestamp(returnDate),
    PlanType: planType.toUpperCase(), // Send uppercase to API (INDIVIDUAL/FAMILY)
    // Terrorism cover (CPER77) is removed from the UI and always sent as false.
    // Mapping: Winter Activities (CPER17) -> WinterSport, Summer Activities (CPER137) -> WaterSport.
    Terrorism: false,
    WaterSport: Boolean(addOns.waterSport),
    WinterSport: Boolean(addOns.winterSport),
    PartyCode: partyCode,
    Channel: getClientSource(),
    isAnnual: Boolean(isAnnual)
  };

  // For logged-in users, add DateOfBirth from profile directly (same as partyCode)
  // This applies to BOTH individual and family plans since partyCode represents the logged-in user
  if (dateOfBirth) {
    console.log("✅ Adding logged-in user DateOfBirth from profile directly");
    console.log("  - planType:", planType);
    console.log("  - dateOfBirth from formData:", dateOfBirth);
    const unixTimestamp = convertToUnixTimestamp(dateOfBirth);
    console.log("  - Converted to unix timestamp:", unixTimestamp);
    payload.DateOfBirth = unixTimestamp;
    console.log("  - Added to payload.DateOfBirth:", payload.DateOfBirth);
  } else {
    console.log("ℹ️ No dateOfBirth in formData - skipping DateOfBirth field");
  }

  // Only include passenger data when explicitly requested (e.g., from passenger info page)
  console.log("🔍 CRITICAL CHECKPOINT - Passenger data inclusion:");
  console.log("- includePassengers:", includePassengers);
  console.log("- passengers:", passengers);
  console.log("- passengers.length:", passengers?.length);

  if (includePassengers && passengers.length > 0) {
    console.log("✅ CONDITION MET: includePassengers && passengers.length > 0");
    console.log("🔍 Passenger data conversion debug:");
    console.log("- planType:", planType);
    console.log("- passengers length:", passengers.length);
    console.log("- passengers[0]:", passengers[0]);

    if (planType === 'individual') {
      // For INDIVIDUAL plan: Add DateOfBirth to main payload, do NOT send Passengers array
      console.log("🔍 INDIVIDUAL PLAN: Adding DateOfBirth to main payload");
      console.log("- passengers[0]:", passengers[0]);
      console.log("- passengers[0]?.dateOfBirth:", passengers[0]?.dateOfBirth);
      console.log("- passengers[0]?.dateOfBirth?.fullDate:", passengers[0]?.dateOfBirth?.fullDate);

      if (passengers[0]?.dateOfBirth && passengers[0].dateOfBirth.fullDate) {
        console.log("✅ Converting DateOfBirth for individual plan:", passengers[0].dateOfBirth.fullDate);
        const unixTimestamp = convertToUnixTimestamp(passengers[0].dateOfBirth);
        console.log("✅ Converted unix timestamp:", unixTimestamp);
        payload.DateOfBirth = unixTimestamp;
        console.log("✅ ADDED DateOfBirth to payload:", payload.DateOfBirth);
      } else {
        console.log("❌ DateOfBirth NOT set - Conditions failed:");
        console.log("  - hasPassenger:", !!passengers[0]);
        console.log("  - hasDateOfBirth:", !!passengers[0]?.dateOfBirth);
        console.log("  - hasFullDate:", !!passengers[0]?.dateOfBirth?.fullDate);
        if (passengers[0]?.dateOfBirth) {
          console.log("  - DateOfBirth object:", passengers[0].dateOfBirth);
          console.log("  - fullDate value:", passengers[0].dateOfBirth.fullDate);
          console.log("  - fullDate type:", typeof passengers[0].dateOfBirth.fullDate);
        }
      }

      // Do NOT include Passengers array for individual plan
      console.log("✅ INDIVIDUAL PLAN: NOT sending Passengers array");

    } else if (planType === 'family') {
      // For FAMILY plan: Send Passengers array but exclude logged-in user
      console.log("🔍 FAMILY PLAN: Preparing Passengers array (excluding logged-in user)");

      // Exclude the logged-in user (first passenger with passengerType: "primary")
      let passengersToSend = passengers;
      const firstPassenger = passengers[0];

      if (firstPassenger?.passengerType === 'primary') {
        console.log('✅ FAMILY PLAN: Excluding logged-in user from passengers array');
        console.log('Logged-in user (excluded):', {
          cpr: firstPassenger.cprNumber,
          name: firstPassenger.fullName,
          passengerType: firstPassenger.passengerType
        });
        // Exclude the first passenger (logged-in user) from the API payload
        passengersToSend = passengers.slice(1);
      }

      console.log('Passengers being sent to API:', passengersToSend.length, 'passengers');

      // Add detailed passenger information (excluding logged-in user)
      payload.Passengers = passengersToSend.map((passenger) => {
        const passengerData = {
          CPR: passenger.cprNumber || '',
          FirstName: passenger.firstName || passenger.fullName?.split(' ')[0] || '',
          LastName: passenger.lastName || passenger.fullName?.split(' ').slice(1).join(' ') || '',
          Gender: passenger.gender === 'Male' || passenger.gender === 'M' ? 'M' : 'F',
          DateOfBirth: convertToUnixTimestamp(passenger.dateOfBirth),
          Nationality: passenger.nationality || '14', // Default Bahrain
          Country: 'BH' // Always Bahrain
        };

        // Add relationship field for family plan passengers
        if (passenger.relationship) {
          passengerData.Relationship = passenger.relationship;
        }

        return passengerData;
      });

      console.log('✅ FAMILY PLAN: Sending', payload.Passengers.length, 'passengers in array');
    }
  } else {
    // For plan selection phase, don't include passenger details
    console.log("❌ CONDITION NOT MET: includePassengers=", includePassengers, ", passengers.length=", passengers?.length);
    console.log("ℹ️ Plan selection phase: Not including passenger data from passengers array");
    // Don't set DateOfBirth to null here - it may have been set from profile for individual plans (line 249)
    // Don't include Passengers array
  }

  // For logged-in users buying for someone else: inject the someone-else's
  // contact details into the top-level payload so the backend creates the
  // policy under their CPR/name, not the auth-token party.
  // Detection: logged-in (isGuest=false) + FALCON partyCode + passengers present.
  if (!isGuest && (!partyCode || partyCode === 'FALCON') && passengers.length > 0) {
    const someoneElse = passengers[0];
    if (someoneElse.cprNumber) payload.CPR = someoneElse.cprNumber;
    if (someoneElse.fullName) payload.FullName = someoneElse.fullName;
    if (someoneElse.emailAddress) payload.Email = someoneElse.emailAddress;
    if (someoneElse.mobileNumber) payload.PhoneNumber = someoneElse.mobileNumber;
    if (someoneElse.gender) {
      payload.Gender = someoneElse.gender === 'Male' || someoneElse.gender === 'M' ? 'M' : 'F';
    }
  }

  // Defensive strip: once a GUEST's CPR is known, remove the FALCON fallback
  // so the backend doesn't mistake it for a known party.
  // For logged-in users buying for someone else, FALCON is intentional and
  // must NOT be stripped — the backend must not fall back to the auth token's party.
  const hasCustomerCpr =
    !!payload.CPR ||
    (Array.isArray(passengers) && passengers.some((p) => p?.cprNumber));
  if ((!partyCode || partyCode === 'FALCON') && hasCustomerCpr && isGuest) {
    delete payload.PartyCode;
  }

  // Guest (not logged-in) overlay: inject contact + passenger details captured
  // on the start page so the final quote/save call carries the customer's info.
  // Logged-in users are unchanged.
  if (isGuest) {
    // Don't send a PartyCode for guests — they are not yet a known party.
    delete payload.PartyCode;
    payload.Branch = "HO";
    payload.PromoCode = "";
    payload.IsFriends = false;

    if (guestInfo) {
      if (guestInfo.cpr) payload.CPR = guestInfo.cpr;
      if (guestInfo.fullName) payload.FullName = guestInfo.fullName;
      if (guestInfo.dateOfBirth) {
        payload.DateOfBirth = convertToUnixTimestamp(guestInfo.dateOfBirth);
      }
      if (guestInfo.email) payload.Email = guestInfo.email;
      if (guestInfo.phoneNumber) payload.PhoneNumber = guestInfo.phoneNumber;
    }

    // For guests, always include the full Passengers array (don't strip the
    // main customer the way we do for logged-in family plans).
    if (passengers.length > 0) {
      payload.Passengers = passengers.map((passenger) => {
        const passengerData = {
          CPR: passenger.cprNumber || passenger.CPR || '',
          FirstName:
            passenger.firstName ||
            passenger.FirstName ||
            passenger.fullName?.split(' ')[0] ||
            '',
          LastName:
            passenger.lastName ||
            passenger.LastName ||
            passenger.fullName?.split(' ').slice(1).join(' ') ||
            '',
          Gender:
            passenger.gender === 'Male' || passenger.gender === 'M' ? 'M' : 'F',
          DateOfBirth: convertToUnixTimestamp(passenger.dateOfBirth),
          Nationality: passenger.nationality || '14',
          Country: 'BH',
        };
        if (passenger.relationship) {
          passengerData.Relationship = passenger.relationship;
        }
        return passengerData;
      });
    }
  }

  console.log("🚀 FINAL PAYLOAD BEING RETURNED:", JSON.stringify(payload, null, 2));
  console.log("🚀 FINAL PAYLOAD HAS DateOfBirth:", !!payload.DateOfBirth, "Value:", payload.DateOfBirth);

  return payload;
};

/**
 * Transform travel API response to match existing premium structure
 * Based on actual API response structure from API_TRAVEL_QUOTE_RESPONSE_EXAMPLE.json
 * @param {Object} apiResponse - API response
 * @returns {Object} Transformed premium data
 */
const transformTravelResponse = (apiResponse) => {
  console.log('=== Transforming API response ===');
  console.log('Raw API response:', apiResponse);
  console.log('TotalPremium from API:', apiResponse.TotalPremium);
  console.log('Premium from API:', apiResponse.Premium);
  console.log('Tax from API:', apiResponse.Tax);
  console.log('QuoteNumber from API:', apiResponse.QuoteDetails?.QuoteNumber);
  console.log('PolicyNumber from API:', apiResponse.PolicyNumber);

  const transformed = {
    // Main pricing info (from root level)
    totalPremium: parseFloat(apiResponse.TotalPremium || 0),
    premium: parseFloat(apiResponse.Premium || 0),
    tax: parseFloat(apiResponse.Tax || 0),
    sumInsured: parseFloat(apiResponse.SumInsured || 0),

    // Policy/Quote info (from root level)
    policyNumber: apiResponse.PolicyNumber || '', // This is actually Quote Number like "Q251502040900017-01"
    quoteNumber: apiResponse.QuoteDetails?.QuoteNumber || apiResponse.PolicyNumber || '',
    planName: apiResponse.PlanName || '',

    // Additional details from QuoteDetails
    planCode: apiResponse.QuoteDetails?.PlanCode || apiResponse.PlanName || '',
    planType: apiResponse.QuoteDetails?.PlanType || '',
    productName: apiResponse.QuoteDetails?.ProductName || '',
    productCode: apiResponse.QuoteDetails?.ProductCode || '',

    // Dates (from QuoteDetails)
    fromDate: apiResponse.QuoteDetails?.FromDate,
    toDate: apiResponse.QuoteDetails?.ToDate,
    fromDateStr: apiResponse.QuoteDetails?.FromDateStr,
    toDateStr: apiResponse.QuoteDetails?.ToDateStr,
    departureDate: apiResponse.QuoteDetails?.DepartureDate,
    returnDate: apiResponse.QuoteDetails?.ReturnDate,

    // Coverage details
    covers: apiResponse.QuoteDetails?.Covers || [],
    Covers: apiResponse.QuoteDetails?.Covers || [],
    passengers: apiResponse.QuoteDetails?.Passengers || [],
    Passengers: apiResponse.QuoteDetails?.Passengers || [],
    terrorism: apiResponse.QuoteDetails?.Terrorism || false,
    waterSport: apiResponse.QuoteDetails?.WaterSport || false,
    winterSport: apiResponse.QuoteDetails?.WinterSport || false,

    // Additional pricing details from QuoteDetails (might be different)
    detailPremium: parseFloat(apiResponse.QuoteDetails?.Premium || 0),
    detailTax: parseFloat(apiResponse.QuoteDetails?.Tax || 0),
    oldPremium: parseFloat(apiResponse.QuoteDetails?.OldPremium || 0),
    oldSumInsured: parseFloat(apiResponse.QuoteDetails?.OldSumInsured || 0),

    // Business details
    partyCode: apiResponse.QuoteDetails?.PartyCode || '',
    sourceOfBusiness: apiResponse.QuoteDetails?.SourceOfBusiness || '',
    statusCode: apiResponse.QuoteDetails?.StatusCode || '',
    modeOfBusiness: apiResponse.QuoteDetails?.ModeOfBusiness || '',

    // Flags
    isRenewable: apiResponse.QuoteDetails?.IsRenewable || false,
    isRenewed: apiResponse.QuoteDetails?.IsRenewed || false,

    // Error handling
    errorMessage: apiResponse.QuoteDetails?.ErrorMessage || '',
    errorCode: apiResponse.ErrorCode || 0,
    points: apiResponse.Points || 0
  };

  console.log('Transformed response:', {
    totalPremium: transformed.totalPremium,
    premium: transformed.premium,
    tax: transformed.tax,
    quoteNumber: transformed.quoteNumber,
    policyNumber: transformed.policyNumber
  });

  return transformed;
};

/**
 * Get travel insurance quote
 * @param {Object} formData - Travel form data
 * @param {string} partyCode - User's party code
 * @param {Object} options - Additional options like includePassengers
 * @returns {Promise<Object>} Quote response
 */
export const getTravelQuote = async (formData, partyCode = 'FALCON', options = {}) => {
  try {
    const { includePassengers = false, timeout = 30000 } = options;

    // Validate required data
    if (!formData.selectedPlan || !formData.departureDate || !formData.returnDate) {
      return {
        success: false,
        error: 'Missing required travel information (plan, dates)',
        data: null
      };
    }

    // Check date validity
    const depDate = new Date(formData.departureDate);
    const retDate = new Date(formData.returnDate);
    if (retDate <= depDate) {
      return {
        success: false,
        error: 'Return date must be after departure date',
        data: null
      };
    }

    // Transform form data with passenger inclusion option
    const requestPayload = transformTravelFormData(formData, partyCode, { includePassengers });
    console.log('=== Travel Quote Request ===');
    console.log('Form data:', formData);
    console.log('Request payload:', requestPayload);
    console.log('Timeout:', timeout);

    const result = await makeAPICall(ENDPOINTS.TRAVEL_QUOTE, requestPayload, {
      timeout: timeout
    });

    if (result.success) {
      const transformedData = transformTravelResponse(result.data);
      console.log('=== Travel Quote Success ===');
      console.log('Transformed data:', transformedData);

      return {
        success: true,
        data: transformedData,
        raw: result.data // Keep original for debugging
      };
    }

    console.error('=== Travel Quote Failed ===');
    console.error('Error:', result.error);
    console.error('Is timeout:', result.isTimeout);
    console.error('Is network error:', result.isNetworkError);

    return {
      success: false,
      error: result.error || 'Failed to get travel quote',
      data: null,
      isNetworkError: result.isNetworkError,
      isServerError: result.isServerError,
      isTimeout: result.isTimeout
    };

  } catch (error) {
    console.error('Travel quote exception:', error);
    return {
      success: false,
      error: error.message || 'Failed to get travel quote',
      data: null
    };
  }
};

/**
 * Get travel quote with caching and debouncing support
 * @param {Object} formData - Travel form data  
 * @param {string} partyCode - User's party code
 * @param {Object} options - Additional options (useCache, forceRefresh, includePassengers)
 * @returns {Promise<Object>} Quote response
 */
// Epoch counter bumped on logout/clear; in-flight responses captured before
// the bump won't write back to the cache, preventing post-logout repopulation.
let _quoteLogoutEpoch = 0;
export const bumpQuoteLogoutEpoch = () => { _quoteLogoutEpoch++; };

const _travelQuoteCache = new Map();
const _travelQuoteDebounceTimers = new Map();
export const getTravelQuoteOptimized = (() => {
  let cache = _travelQuoteCache;
  let debounceTimers = _travelQuoteDebounceTimers;
  // Removed activeControllers to prevent conflicts with makeAPICall's AbortController

  const getCacheKey = (formData, partyCode, includePassengers = false) => {
    const key = {
      plan: formData.selectedPlan,
      departure: formData.departureDate,
      return: formData.returnDate,
      planType: formData.planType, // Include explicit planType
      passengersCount: includePassengers ? (formData.passengers?.length || 0) : 0,
      includePassengers: includePassengers,
      terrorism: formData.addOns?.terrorism || false,
      waterSport: formData.addOns?.waterSport || false,
      winterSport: formData.addOns?.winterSport || false,
      party: partyCode
    };
    return JSON.stringify(key);
  };

  return async (formData, partyCode = 'FALCON', options = {}) => {
    const {
      useCache = true,
      forceRefresh = false,
      debounceMs = 0,
      includePassengers = false // Default to false for plan selection
    } = options;

    const cacheKey = getCacheKey(formData, partyCode, includePassengers);
    const _startEpoch = _quoteLogoutEpoch;

    console.log('=== getTravelQuoteOptimized ===');
    console.log('Cache key:', cacheKey);
    console.log('Force refresh:', forceRefresh);
    console.log('Debounce ms:', debounceMs);

    if (forceRefresh) {
      console.log('🔥 FORCE REFRESH: Bypassing cache due to forceRefresh=true');
      console.log('🔥 This usually happens when planType or add-ons change');
    }

    // Return cached result if available and not forcing refresh
    if (useCache && !forceRefresh && cache.has(cacheKey)) {
      const cached = cache.get(cacheKey);
      // Cache for 5 minutes
      if (Date.now() - cached.timestamp < 5 * 60 * 1000) {
        console.log('✅ Using cached travel quote (optimized)');
        return cached.data;
      } else {
        console.log('❌ Cache expired, removing');
        cache.delete(cacheKey);
      }
    }

    // Handle debouncing for real-time updates
    if (debounceMs > 0) {
      console.log(`⏳ Debouncing API call for ${debounceMs}ms`);
      return new Promise((resolve) => {
        if (debounceTimers.has(cacheKey)) {
          clearTimeout(debounceTimers.get(cacheKey));
          console.log('🔄 Cleared previous debounce timer');
        }

        const timer = setTimeout(async () => {
          debounceTimers.delete(cacheKey);
          console.log('🚀 Debounce timer fired, making API call');

          const result = await getTravelQuote(formData, partyCode, { includePassengers });

          // Cache successful results — skip if a logout happened since dispatch
          if (result.success && useCache && _startEpoch === _quoteLogoutEpoch) {
            console.log('💾 Caching successful result');
            cache.set(cacheKey, {
              data: result,
              timestamp: Date.now()
            });
          }

          resolve(result);
        }, debounceMs);

        debounceTimers.set(cacheKey, timer);
      });
    }

    console.log('🚀 Making direct API call (no debounce)');

    try {
      // Direct API call with increased timeout
      const result = await getTravelQuote(formData, partyCode, {
        includePassengers,
        timeout: 30000 // 30 second timeout
      });

      console.log('✅ Direct API call completed');

      // Cache successful results — skip if a logout happened since dispatch
      if (result.success && useCache && _startEpoch === _quoteLogoutEpoch) {
        console.log('💾 Caching successful result');
        cache.set(cacheKey, {
          data: result,
          timestamp: Date.now()
        });
      }

      return result;
    } catch (error) {
      console.error('❌ Direct API call failed:', error);
      throw error;
    }
  };
})();

/**
 * Clear travel quote cache and cancel pending requests
 */
export const clearTravelQuoteCache = () => {
  _quoteLogoutEpoch++;
  try {
    _travelQuoteCache.clear();
    _travelQuoteDebounceTimers.forEach((t) => clearTimeout(t));
    _travelQuoteDebounceTimers.clear();
  } catch (e) {}
};

/**
 * Cancel all pending travel quote requests
 */
export const cancelAllTravelQuoteRequests = () => {
  try {
    _travelQuoteDebounceTimers.forEach((t) => clearTimeout(t));
    _travelQuoteDebounceTimers.clear();
  } catch (e) {}
};

export const getBniTravelPlanPrices = async (formData, options = {}) => {
  try {
    const { timeout = 30000 } = options;

    if (!formData.departureDate || !formData.returnDate || !formData.planType) {
      return {
        success: false,
        error: 'Missing required travel information (dates, planType)',
        data: null
      };
    }

    const fromDate = convertToUnixTimestamp(formData.departureDate);
    const toDate = convertToUnixTimestamp(formData.returnDate);

    if (!fromDate || !toDate) {
      return {
        success: false,
        error: 'Invalid date format',
        data: null
      };
    }

    let requestPayload;

    if (formData.planType === 'individual') {
      requestPayload = {
        fromDate: fromDate,
        toDate: toDate,
        policyType: 'INDIVIDUAL',
        isAnnual: Boolean(formData.isAnnual)
      };
    } else if (formData.planType === 'family') {
      const familyMembersCount = formData.passengers?.length || 2;
      requestPayload = {
        fromDate: fromDate,
        toDate: toDate,
        policyType: 'FAMILY',
        members: familyMembersCount,
        isAnnual: Boolean(formData.isAnnual)
      };
    } else {
      return {
        success: false,
        error: 'Invalid plan type. Must be "individual" or "family"',
        data: null
      };
    }

    console.log('=== BNI Travel Plan Prices Request ===');
    console.log('Request payload:', requestPayload);

    const result = await makeAPICall(ENDPOINTS.BNI_TRAVEL_PLAN_PRICES, requestPayload, {
      timeout: timeout
    });

    if (result.success) {
      console.log('=== BNI Travel Plan Prices Success ===');
      return {
        success: true,
        data: result.data,
        raw: result.data
      };
    }

    console.error('=== BNI Travel Plan Prices Failed ===');
    return {
      success: false,
      error: result.error || 'Failed to get BNI travel plan prices',
      data: null,
      isNetworkError: result.isNetworkError,
      isServerError: result.isServerError,
      isTimeout: result.isTimeout
    };

  } catch (error) {
    console.error('BNI travel plan prices exception:', error);
    return {
      success: false,
      error: error.message || 'Failed to get BNI travel plan prices',
      data: null
    };
  }
};

export const getBniTravelQuote = async (formData, partyCode = 'FALCON', options = {}) => {
  try {
    const { includePassengers = false, timeout = 30000 } = options;

    if (!formData.selectedPlan || !formData.departureDate || !formData.returnDate) {
      return {
        success: false,
        error: 'Missing required travel information (plan, dates)',
        data: null
      };
    }

    const depDate = new Date(formData.departureDate);
    const retDate = new Date(formData.returnDate);
    if (retDate <= depDate) {
      return {
        success: false,
        error: 'Return date must be after departure date',
        data: null
      };
    }

    const requestPayload = transformTravelFormData(formData, partyCode, { includePassengers });

    const bniPlanCodeMapping = {
      'schengen': 'SCHENGENB',
      'worldwide_ex': 'WLEXB',
      'worldwide': 'WLB',
      'schengenb': 'SCHENGENB',
      'wlexb': 'WLEXB',
      'wlb': 'WLB',
    };
    const selectedPlanLower = (formData.selectedPlan || '').toLowerCase();
    const mappedPlanCode = bniPlanCodeMapping[selectedPlanLower];
    if (!mappedPlanCode) {
      return {
        success: false,
        error: `Invalid BNI plan code: ${formData.selectedPlan}. Valid options are: schengen, worldwide_ex, worldwide.`,
        data: null
      };
    }
    requestPayload.PlanCode = mappedPlanCode;

    console.log('=== BNI Travel Quote Request ===');
    console.log('Form data:', formData);
    console.log('Request payload:', requestPayload);

    const result = await makeAPICall(ENDPOINTS.BNI_TRAVEL_QUOTE, requestPayload, {
      timeout: timeout
    });

    if (result.success) {
      const transformedData = transformTravelResponse(result.data);
      console.log('=== BNI Travel Quote Success ===');
      return {
        success: true,
        data: transformedData,
        raw: result.data
      };
    }

    console.error('=== BNI Travel Quote Failed ===');
    return {
      success: false,
      error: result.error || 'Failed to get BNI travel quote',
      data: null,
      isNetworkError: result.isNetworkError,
      isServerError: result.isServerError,
      isTimeout: result.isTimeout
    };

  } catch (error) {
    console.error('BNI travel quote exception:', error);
    return {
      success: false,
      error: error.message || 'Failed to get BNI travel quote',
      data: null
    };
  }
};

const _bniTravelQuoteCache = new Map();
const _bniTravelQuoteDebounceTimers = new Map();
export const getBniTravelQuoteOptimized = (() => {
  let cache = _bniTravelQuoteCache;
  let debounceTimers = _bniTravelQuoteDebounceTimers;

  const getCacheKey = (formData, partyCode, includePassengers = false) => {
    const key = {
      plan: formData.selectedPlan,
      departure: formData.departureDate,
      return: formData.returnDate,
      planType: formData.planType,
      passengersCount: includePassengers ? (formData.passengers?.length || 0) : 0,
      includePassengers: includePassengers,
      terrorism: formData.addOns?.terrorism || false,
      waterSport: formData.addOns?.waterSport || false,
      winterSport: formData.addOns?.winterSport || false,
      party: partyCode
    };
    return JSON.stringify(key);
  };

  return async (formData, partyCode = 'FALCON', options = {}) => {
    const {
      useCache = true,
      forceRefresh = false,
      debounceMs = 0,
      includePassengers = false
    } = options;

    const cacheKey = getCacheKey(formData, partyCode, includePassengers);
    const _startEpoch = _quoteLogoutEpoch;

    if (useCache && !forceRefresh && cache.has(cacheKey)) {
      const cached = cache.get(cacheKey);
      if (Date.now() - cached.timestamp < 5 * 60 * 1000) {
        console.log('Using cached BNI travel quote');
        return cached.data;
      } else {
        cache.delete(cacheKey);
      }
    }

    if (debounceMs > 0) {
      return new Promise((resolve) => {
        if (debounceTimers.has(cacheKey)) {
          clearTimeout(debounceTimers.get(cacheKey));
        }

        const timer = setTimeout(async () => {
          debounceTimers.delete(cacheKey);
          const result = await getBniTravelQuote(formData, partyCode, { includePassengers });
          if (result.success && useCache && _startEpoch === _quoteLogoutEpoch) {
            cache.set(cacheKey, { data: result, timestamp: Date.now() });
          }
          resolve(result);
        }, debounceMs);

        debounceTimers.set(cacheKey, timer);
      });
    }

    try {
      const result = await getBniTravelQuote(formData, partyCode, {
        includePassengers,
        timeout: 30000
      });

      if (result.success && useCache && _startEpoch === _quoteLogoutEpoch) {
        cache.set(cacheKey, { data: result, timestamp: Date.now() });
      }

      return result;
    } catch (error) {
      console.error('BNI travel quote optimized call failed:', error);
      throw error;
    }
  };
})();

/**
 * Transform home form data to API request format
 * @param {Object} homeFormData - Complete home form data from Redux
 * @param {string} partyCode - User's party code
 * @returns {Object} API request payload
 */
const transformHomeFormData = (homeFormData, partyCode = 'FALCON') => {
  const { homeDetails, addressDetails, covers } = homeFormData;

  console.log('=== DEBUGGING HOME FORM DATA TRANSFORMATION ===');
  console.log('Raw homeFormData:', homeFormData);
  console.log('homeDetails:', homeDetails);
  console.log('addressDetails:', addressDetails);
  console.log('covers:', covers);

  // Helper function to clean and parse numeric values (removes commas and spaces)
  const parseCleanFloat = (value) => {
    if (!value || value === '') return 0;
    const cleaned = String(value).replace(/[,\s]/g, '');
    const parsed = parseFloat(cleaned);
    console.log(`parseCleanFloat: "${value}" -> "${cleaned}" -> ${parsed}`);
    return isNaN(parsed) ? 0 : parsed;
  };

  // Calculate sum insured values - ensure we're getting raw values properly
  const buildingValueRaw = homeDetails?.buildingValue;
  const contentsValueRaw = homeDetails?.contentsValue;
  const personalValueRaw = homeDetails?.personalBelongingsValue;

  console.log('Raw field values from form:');
  console.log('- buildingValue (raw):', buildingValueRaw, typeof buildingValueRaw);
  console.log('- contentsValue (raw):', contentsValueRaw, typeof contentsValueRaw);
  console.log('- personalBelongingsValue (raw):', personalValueRaw, typeof personalValueRaw);
  console.log('- contentsDetails enabled:', homeDetails?.contentsDetails);

  const fixValue = parseCleanFloat(buildingValueRaw);
  // Content Details section is always visible now, so always include these values
  const contentValue = parseCleanFloat(contentsValueRaw);
  const personalValue = parseCleanFloat(personalValueRaw);
  const sumInsured = fixValue + contentValue + personalValue;

  console.log('Parsed values after cleaning:');
  console.log('- fixValue (buildingValue parsed):', fixValue, typeof fixValue);
  console.log('- contentValue (parsed):', contentValue, typeof contentValue);
  console.log('- personalValue (parsed):', personalValue, typeof personalValue);
  console.log('- sumInsured (total):', sumInsured, typeof sumInsured);

  // Validate that values are reasonable
  if (fixValue <= 0) {
    console.warn('⚠️ Building value is 0 or negative:', fixValue);
  }
  if (sumInsured <= 0) {
    console.warn('⚠️ Total sum insured is 0 or negative:', sumInsured);
  }

  // Create address description
  const description = `Building ${addressDetails?.buildingNumber || ''}, Block ${addressDetails?.blockNumber || ''}, Road ${addressDetails?.roadNumber || ''}`.trim();

  console.log('Address details:');
  console.log('- buildingNumber:', addressDetails?.buildingNumber, typeof addressDetails?.buildingNumber);
  console.log('- blockNumber:', addressDetails?.blockNumber, typeof addressDetails?.blockNumber);
  console.log('- roadNumber:', addressDetails?.roadNumber, typeof addressDetails?.roadNumber);
  console.log('- buildingAge:', addressDetails?.buildingAge, typeof addressDetails?.buildingAge);
  console.log('- description:', description);

  // Ensure all numeric values are proper numbers
  const age = parseInt(addressDetails?.buildingAge || 0);
  const block = parseInt(addressDetails?.blockNumber || 0);
  const road = parseInt(addressDetails?.roadNumber || 0);
  const fromDate = getCurrentUnixTimestamp();

  console.log('Address parsing:');
  console.log('- buildingAge raw:', addressDetails?.buildingAge, '-> parsed:', age);
  console.log('- blockNumber raw:', addressDetails?.blockNumber, '-> parsed:', block);
  console.log('- roadNumber raw:', addressDetails?.roadNumber, '-> parsed:', road);
  console.log('- fromDate (timestamp):', fromDate);

  // Extract cover selections (support both dynamic keys and legacy keys)
  const allRisk = covers?.allRisk || covers?.cpro21 || false;
  const domesticHelper = covers?.domesticHelper || covers?.cpro35 || false;
  const vipSelected = covers?.vipCover || covers?.cpro13 || false;
  const terrorismSelected = covers?.sabotageAndTerrorism || covers?.cpro33 || false;

  // When All Risk is selected, force VIP and Terrorism to false
  // When All Risk is NOT selected, send the actual user selection
  const vip = allRisk ? false : vipSelected;
  const terrorism = allRisk ? false : terrorismSelected;

  console.log('Cover selections:');
  console.log('- AllRisk:', allRisk);
  console.log('- DomesticHelper:', domesticHelper);
  console.log('- VIP selected in UI:', vipSelected, '-> sending to API:', vip);
  console.log('- Terrorism selected in UI:', terrorismSelected, '-> sending to API:', terrorism);

  const payload = {
    Age: age,
    AllRisk: allRisk,
    Block: block,
    Building: addressDetails?.buildingNumber || '',
    Channel: getClientSource(),
    ContentValue: contentValue,
    Description: description,
    DomesticHelper: domesticHelper,
    FixValue: fixValue,
    FromDate: fromDate,
    PartyCode: partyCode,
    PersonalValue: personalValue,
    Road: road,
    SumInsured: sumInsured,
    Terrorism: terrorism,
    VIP: vip
  };

  // Note: home quote payload does not carry the customer's CPR — keep
  // FALCON as the guest fallback for this product.

  // Add domestic helper specific fields when domestic helper is enabled
  if (domesticHelper && homeFormData.domesticHelperCount) {
    payload.domesticHelperCount = homeFormData.domesticHelperCount;
    payload.domesticHelperLimit = homeFormData.domesticHelperLimit;
    payload.domesticMembers = homeFormData.domesticMembers;

    console.log('🏠 Adding domestic helper data to payload:');
    console.log('- domesticHelperCount:', payload.domesticHelperCount);
    console.log('- domesticHelperLimit:', payload.domesticHelperLimit);
    console.log('- domesticMembers:', payload.domesticMembers);
  }

  // Validate that all numeric fields are valid
  const numericFields = ['Age', 'Block', 'ContentValue', 'FixValue', 'FromDate', 'PersonalValue', 'Road', 'SumInsured'];
  let allValid = true;
  numericFields.forEach(field => {
    if (typeof payload[field] !== 'number' || isNaN(payload[field])) {
      console.error(`❌ Field ${field} is not a valid number:`, payload[field], typeof payload[field]);
      allValid = false;
    } else {
      console.log(`✅ Field ${field} is valid:`, payload[field]);
    }
  });

  console.log('=== FINAL API PAYLOAD ===');
  console.log('All fields valid:', allValid);
  console.log(JSON.stringify(payload, null, 2));

  // Additional validation for suspicious values
  if (fixValue === sumInsured && contentValue === 0 && personalValue === 0) {
    console.log('ℹ️ Only building value provided (no contents)');
  }

  // Check if values seem too similar (possible parsing issue)
  if (fixValue > 0 && contentValue > 0 && personalValue > 0) {
    if (fixValue === contentValue || fixValue === personalValue || contentValue === personalValue) {
      console.warn('⚠️ Suspicious: multiple values are identical, possible parsing issue');
    }
  }

  console.log('=== END PAYLOAD DEBUG ===');

  return payload;
};

/**
 * Transform home API response to match existing premium structure
 * @param {Object} apiResponse - API response
 * @returns {Object} Transformed premium data
 */
const transformHomeResponse = (apiResponse) => {
  console.log('=== Transforming Home API response ===');
  console.log('Raw API response:', apiResponse);

  const transformed = {
    // Main pricing info (from root level)
    totalPremium: parseFloat(apiResponse.TotalPremium || 0),
    premium: parseFloat(apiResponse.Premium || 0),
    tax: parseFloat(apiResponse.Tax || 0),
    sumInsured: parseFloat(apiResponse.SumInsured || 0),

    // Policy/Quote info (from root level)
    policyNumber: apiResponse.PolicyNumber || '',
    quoteNumber: apiResponse.QuoteDetails?.QuoteNumber || apiResponse.PolicyNumber || '',

    // Additional details from QuoteDetails
    productName: apiResponse.QuoteDetails?.ProductName || '',
    productCode: apiResponse.QuoteDetails?.ProductCode || '',

    // Dates (from QuoteDetails)
    fromDate: apiResponse.QuoteDetails?.FromDate,
    toDate: apiResponse.QuoteDetails?.ToDate,
    fromDateStr: apiResponse.QuoteDetails?.FromDateStr,
    toDateStr: apiResponse.QuoteDetails?.ToDateStr,

    // Coverage details
    covers: apiResponse.QuoteDetails?.Covers || [],

    // Additional pricing details from QuoteDetails
    detailPremium: parseFloat(apiResponse.QuoteDetails?.Premium || 0),
    detailTax: parseFloat(apiResponse.QuoteDetails?.Tax || 0),
    oldPremium: parseFloat(apiResponse.QuoteDetails?.OldPremium || 0),
    oldSumInsured: parseFloat(apiResponse.QuoteDetails?.OldSumInsured || 0),

    // Business details
    partyCode: apiResponse.QuoteDetails?.PartyCode || '',
    sourceOfBusiness: apiResponse.QuoteDetails?.SourceOfBusiness || '',
    statusCode: apiResponse.QuoteDetails?.StatusCode || '',
    modeOfBusiness: apiResponse.QuoteDetails?.ModeOfBusiness || '',

    // Property details
    building: apiResponse.QuoteDetails?.Building || '',
    road: apiResponse.QuoteDetails?.Road || '',
    block: apiResponse.QuoteDetails?.Block || '',
    area: apiResponse.QuoteDetails?.Area || '',
    city: apiResponse.QuoteDetails?.City || '',
    country: apiResponse.QuoteDetails?.Country || '',
    description: apiResponse.QuoteDetails?.Description || '',
    age: apiResponse.QuoteDetails?.Age || '',

    // Values breakdown
    fixValue: parseFloat(apiResponse.QuoteDetails?.FixValue || 0),
    contentValue: parseFloat(apiResponse.QuoteDetails?.ContentValue || 0),
    personalValue: parseFloat(apiResponse.QuoteDetails?.PersonalValue || 0),
    rentValue: parseFloat(apiResponse.QuoteDetails?.RentValue || 0),

    // Party information
    party: apiResponse.QuoteDetails?.Party || null,

    // Flags
    isRenewable: apiResponse.QuoteDetails?.IsRenewable || false,
    isRenewed: apiResponse.QuoteDetails?.IsRenewed || false,
    defaultCovers: apiResponse.QuoteDetails?.DefaultCovers || false,
    allCovers: apiResponse.QuoteDetails?.AllCovers || false,

    // Error handling
    errorMessage: apiResponse.QuoteDetails?.ErrorMessage || '',
    errorCode: apiResponse.ErrorCode || 0,
    points: apiResponse.Points || 0
  };

  console.log('Transformed home response:', {
    totalPremium: transformed.totalPremium,
    premium: transformed.premium,
    tax: transformed.tax,
    quoteNumber: transformed.quoteNumber,
    policyNumber: transformed.policyNumber
  });

  return transformed;
};

/**
 * Get home insurance quote
 * @param {Object} homeFormData - Complete home form data from Redux
 * @param {string} partyCode - User's party code
 * @param {Object} options - Additional options
 * @returns {Promise<Object>} Quote response
 */
export const getHomeQuote = async (homeFormData, partyCode = 'FALCON', options = {}) => {
  try {
    const { timeout = 30000 } = options;

    // Validate required data
    if (!homeFormData.homeDetails?.buildingValue) {
      return {
        success: false,
        error: 'Building value is required',
        data: null
      };
    }

    if (!homeFormData.addressDetails?.buildingNumber ||
      !homeFormData.addressDetails?.roadNumber ||
      !homeFormData.addressDetails?.blockNumber ||
      !homeFormData.addressDetails?.buildingAge) {
      return {
        success: false,
        error: 'Complete address details are required',
        data: null
      };
    }

    // Validate sum insured limit
    const fixValue = parseFloat(homeFormData.homeDetails.buildingValue || 0);
    // Content Details section is always visible now, so always include these values
    const contentValue = parseFloat(homeFormData.homeDetails.contentsValue || 0);
    const personalValue = parseFloat(homeFormData.homeDetails.personalBelongingsValue || 0);
    const sumInsured = fixValue + contentValue + personalValue;

    if (sumInsured > 500000) {
      return {
        success: false,
        error: 'Total sum insured must be less than BHD 500,000',
        data: null
      };
    }

    // Transform form data
    const requestPayload = transformHomeFormData(homeFormData, partyCode);
    console.log('=== Home Quote Request ===');
    console.log('Form data:', homeFormData);
    console.log('Request payload:', requestPayload);
    console.log('Timeout:', timeout);

    const result = await makeAPICall(ENDPOINTS.HOME_QUOTE, requestPayload, {
      timeout: timeout
    });

    if (result.success) {
      const transformedData = transformHomeResponse(result.data);
      console.log('=== Home Quote Success ===');
      console.log('Transformed data:', transformedData);

      return {
        success: true,
        data: transformedData,
        raw: result.data // Keep original for debugging
      };
    }

    console.error('=== Home Quote Failed ===');
    console.error('Error:', result.error);
    console.error('Is timeout:', result.isTimeout);
    console.error('Is network error:', result.isNetworkError);

    return {
      success: false,
      error: result.error || 'Failed to get home quote',
      data: null,
      isNetworkError: result.isNetworkError,
      isServerError: result.isServerError,
      isTimeout: result.isTimeout
    };

  } catch (error) {
    console.error('Home quote exception:', error);
    return {
      success: false,
      error: error.message || 'Failed to get home quote',
      data: null
    };
  }
};

/**
 * Optimized home quote with caching and deduplication
 * Similar to travel quote optimization
 */
const _homeQuoteCache = new Map();
const _homeQuotePendingRequests = new Map();
export const getHomeQuoteOptimized = (() => {
  let cache = _homeQuoteCache;
  let pendingRequests = _homeQuotePendingRequests;

  const generateCacheKey = (homeFormData, partyCode) => {
    const key = JSON.stringify({
      homeDetails: homeFormData.homeDetails,
      addressDetails: homeFormData.addressDetails,
      covers: homeFormData.covers,
      partyCode
    });
    // Base64 encode for shorter key - handle Unicode characters (Arabic text)
    try {
      return btoa(unescape(encodeURIComponent(key)));
    } catch (error) {
      console.error('Error encoding cache key:', error);
      // Fallback: use a simple hash if btoa fails
      return key.split('').reduce((hash, char) => {
        return ((hash << 5) - hash) + char.charCodeAt(0);
      }, 0).toString(16);
    }
  };

  return async (homeFormData, partyCode = 'FALCON', options = {}) => {
    const { timeout = 30000, forceRefresh = false } = options;
    const cacheKey = generateCacheKey(homeFormData, partyCode);
    const _startEpoch = _quoteLogoutEpoch;

    console.log('=== Home Quote Optimized ===');
    console.log('Cache key:', cacheKey.substring(0, 20) + '...');
    console.log('Force refresh:', forceRefresh);
    console.log('Cache size:', cache.size);
    console.log('Pending requests:', pendingRequests.size);

    // Check cache first (unless force refresh)
    if (!forceRefresh && cache.has(cacheKey)) {
      console.log('✅ Cache hit - returning cached home quote');
      return cache.get(cacheKey);
    }

    // Check if same request is already pending
    if (pendingRequests.has(cacheKey)) {
      console.log('⏳ Request already pending - waiting for existing call');
      return await pendingRequests.get(cacheKey);
    }

    // Make new request
    console.log('🆕 Making new home quote request');
    const requestPromise = (async () => {
      try {
        const result = await getHomeQuote(homeFormData, partyCode, { timeout });
        
        // Cache successful results — skip if a logout happened since dispatch
        if (result.success && _startEpoch === _quoteLogoutEpoch) {
          console.log('💾 Caching home quote result');
          cache.set(cacheKey, result);
          
          // Clean old cache entries (keep last 10)
          if (cache.size > 10) {
            const oldestKey = cache.keys().next().value;
            cache.delete(oldestKey);
            console.log('🧹 Cleaned oldest cache entry');
          }
        }

        return result;
      } catch (error) {
        console.error('❌ Home quote request failed:', error);
        throw error;
      } finally {
        // Remove from pending requests
        pendingRequests.delete(cacheKey);
      }
    })();

    // Store pending request
    pendingRequests.set(cacheKey, requestPromise);

    return await requestPromise;
  };
})();

/**
 * Clear home quote cache and cancel pending requests
 */
export const clearHomeQuoteCache = () => {
  _quoteLogoutEpoch++;
  try {
    _homeQuoteCache.clear();
    _homeQuotePendingRequests.clear();
  } catch (e) {}
};

/**
 * Clear BNI travel quote cache and pending debounces
 */
export const clearBniTravelQuoteCache = () => {
  _quoteLogoutEpoch++;
  try {
    _bniTravelQuoteCache.clear();
    _bniTravelQuoteDebounceTimers.forEach((t) => clearTimeout(t));
    _bniTravelQuoteDebounceTimers.clear();
  } catch (e) {}
};

/**
 * Transform Personal Cyber form data to API request format
 * Based on ENDPOINTS.md specification:
 * PolicyPlan options: OPTION1 (500 USD), OPTION2 (1000 USD), OPTION3 (2000 USD), OPTION4 (3500 USD), OPTION5 (5000 USD)
 * @param {Object} personalCyberFormData - Complete Personal Cyber form data from Redux
 * @param {string} partyCode - User's party code (default FALCON for guests)
 * @returns {Object} API request payload
 */
const transformPersonalCyberFormData = (personalCyberFormData, partyCode = 'FALCON') => {
  const {
    PERSONAL_CYBER_SUM_INSURED_FORM,
    PERSONAL_CYBER_CONTACT_DETAILS_FORM,
    PERSONAL_CYBER_BASIC_INFO_FORM,
  } = personalCyberFormData;

  console.log('=== DEBUGGING PERSONAL CYBER FORM DATA TRANSFORMATION ===');
  console.log('Raw personalCyberFormData:', personalCyberFormData);
  console.log('Sum insured form:', PERSONAL_CYBER_SUM_INSURED_FORM);
  console.log('Contact details form:', PERSONAL_CYBER_CONTACT_DETAILS_FORM);

  // Get selected sum insured value (e.g., "3300", "4400", etc.)
  const selectedSumInsured = PERSONAL_CYBER_SUM_INSURED_FORM?.selectedSumInsured;

  // Map sum insured values to PolicyPlan options
  const policyPlanMapping = {
    '3300': 'OPTION1',   // 500 USD
    '4400': 'OPTION2',   // 1000 USD
    '6600': 'OPTION3',   // 2000 USD
    '8800': 'OPTION4',   // 3500 USD
    '11000': 'OPTION5'   // 5000 USD
  };

  const policyPlan = policyPlanMapping[selectedSumInsured] || 'OPTION2'; // Default to OPTION2 (1000 USD)
  console.log(`Policy plan mapping: ${selectedSumInsured} -> ${policyPlan}`);

  // Get passenger (customer) data - always use first passenger.
  // Two callers use different field names: contactDetails uses 'insuredPersons',
  // planSelection/contactDetails uses 'passengers'. Handle both.
  const passengers =
    PERSONAL_CYBER_CONTACT_DETAILS_FORM?.insuredPersons ||
    PERSONAL_CYBER_CONTACT_DETAILS_FORM?.passengers ||
    [];
  const customer = passengers[0] || {};

  console.log('Customer data:', customer);

  // Format date of birth to dd/mm/yyyy
  const formatDateOfBirth = (dateOfBirth) => {
    if (!dateOfBirth || !dateOfBirth.fullDate) return '';

    const { day, month, year } = dateOfBirth;
    if (!day || !month || !year) return '';

    // Ensure 2-digit format for day and month
    const formattedDay = day.toString().padStart(2, '0');
    const formattedMonth = month.toString().padStart(2, '0');

    return `${formattedDay}/${formattedMonth}/${year}`;
  };

  const dateOfBirth = formatDateOfBirth(customer.dateOfBirth);
  console.log('Formatted date of birth:', dateOfBirth);

  // Defensive overlay: if a passenger field is missing but the start-page
  // basic info has the value, fall back to the basic info so guest customer
  // details aren't dropped from the payload.
  const basic = PERSONAL_CYBER_BASIC_INFO_FORM || {};
  let basicDob = '';
  if (basic.birthDate) {
    const [y, m, d] = basic.birthDate.split('-');
    if (y && m && d) {
      basicDob = `${d.padStart(2, '0')}/${m.padStart(2, '0')}/${y}`;
    }
  }

  const payload = {
    partyCode: partyCode,
    fullName: customer.fullName || basic.fullName || '',
    DateOfBirth: dateOfBirth || basicDob,
    CPR: customer.cprNumber || basic.cpr || '',
    PhoneNumber: customer.mobileNumber || basic.contactNumber || '',
    PolicyPlan: policyPlan,
    Email: customer.emailAddress || basic.email || ''
  };

  // Once we have the customer's CPR, don't leak the FALCON guest fallback.
  // Exception: for logged-in users buying for someone else, FALCON is intentional
  // — must not fall back to the auth token's party.
  // Two callers store the policy-for value under different keys:
  //   contactDetails  -> policyType ('for_someone_else' | 'for_myself')
  //   planSelection   -> policyFor  ('someone_else'     | 'myself')
  const cyberPolicyFor =
    PERSONAL_CYBER_CONTACT_DETAILS_FORM?.policyType ||
    PERSONAL_CYBER_CONTACT_DETAILS_FORM?.policyFor;
  const cyberIsSomeoneElse =
    cyberPolicyFor === 'for_someone_else' || cyberPolicyFor === 'someone_else';
  if ((!partyCode || partyCode === 'FALCON') && payload.CPR && !cyberIsSomeoneElse) {
    delete payload.partyCode;
  }

  console.log('=== FINAL PERSONAL CYBER API PAYLOAD ===');
  console.log(JSON.stringify(payload, null, 2));
  console.log('=== END PAYLOAD DEBUG ===');

  return payload;
};

/**
 * Transform Personal Cyber API response to match existing premium structure
 * @param {Object} apiResponse - API response
 * @returns {Object} Transformed premium data
 */
const transformPersonalCyberResponse = (apiResponse) => {
  console.log('=== Transforming Personal Cyber API response ===');
  console.log('Raw API response:', apiResponse);

  const transformed = {
    // Main pricing info (from root level)
    totalPremium: parseFloat(apiResponse.TotalPremium || 0),
    premium: parseFloat(apiResponse.Premium || 0),
    tax: parseFloat(apiResponse.Tax || 0),
    sumInsured: parseFloat(apiResponse.SumInsured || 0),

    // Policy/Quote info (from root level)
    policyNumber: apiResponse.PolicyNumber || '',
    quoteNumber: apiResponse.QuoteDetails?.QuoteNumber || apiResponse.PolicyNumber || '',

    // Additional details from QuoteDetails
    productName: apiResponse.QuoteDetails?.ProductName || '',
    productCode: apiResponse.QuoteDetails?.ProductCode || '',
    policyPlan: apiResponse.QuoteDetails?.PolicyPlan || '',

    // Dates (from QuoteDetails)
    fromDate: apiResponse.QuoteDetails?.FromDate,
    toDate: apiResponse.QuoteDetails?.ToDate,
    fromDateStr: apiResponse.QuoteDetails?.FromDateStr,
    toDateStr: apiResponse.QuoteDetails?.ToDateStr,

    // Coverage details
    covers: apiResponse.QuoteDetails?.Covers || [],

    // Customer details
    customer: apiResponse.QuoteDetails?.Customer || null,
    fullName: apiResponse.QuoteDetails?.FullName || '',
    cpr: apiResponse.QuoteDetails?.CPR || '',
    email: apiResponse.QuoteDetails?.Email || '',
    phoneNumber: apiResponse.QuoteDetails?.PhoneNumber || '',
    dateOfBirth: apiResponse.QuoteDetails?.DateOfBirth || '',

    // Additional pricing details from QuoteDetails
    detailPremium: parseFloat(apiResponse.QuoteDetails?.Premium || 0),
    detailTax: parseFloat(apiResponse.QuoteDetails?.Tax || 0),
    oldPremium: parseFloat(apiResponse.QuoteDetails?.OldPremium || 0),
    oldSumInsured: parseFloat(apiResponse.QuoteDetails?.OldSumInsured || 0),

    // Business details
    partyCode: apiResponse.QuoteDetails?.PartyCode || '',
    sourceOfBusiness: apiResponse.QuoteDetails?.SourceOfBusiness || '',
    statusCode: apiResponse.QuoteDetails?.StatusCode || '',
    modeOfBusiness: apiResponse.QuoteDetails?.ModeOfBusiness || '',

    // Flags
    isRenewable: apiResponse.QuoteDetails?.IsRenewable || false,
    isRenewed: apiResponse.QuoteDetails?.IsRenewed || false,

    // Error handling
    errorMessage: apiResponse.QuoteDetails?.ErrorMessage || '',
    errorCode: apiResponse.ErrorCode || 0,
    points: apiResponse.Points || 0
  };

  console.log('Transformed Personal Cyber response:', {
    totalPremium: transformed.totalPremium,
    premium: transformed.premium,
    tax: transformed.tax,
    quoteNumber: transformed.quoteNumber,
    policyNumber: transformed.policyNumber,
    policyPlan: transformed.policyPlan
  });

  return transformed;
};

/**
 * Get Personal Cyber insurance quote
 * @param {Object} personalCyberFormData - Complete Personal Cyber form data from Redux
 * @param {string} partyCode - User's party code (default FALCON for guests)
 * @param {Object} options - Additional options
 * @returns {Promise<Object>} Quote response
 */
export const getPersonalCyberQuote = async (personalCyberFormData, partyCode = 'FALCON', options = {}) => {
  try {
    const { timeout = 30000 } = options;

    // Validate required data - check both form constant names
    const sumInsuredData = personalCyberFormData.PERSONAL_CYBER_SUM_INSURED_FORM || personalCyberFormData.sumInsuredForm;
    const contactDetailsData = personalCyberFormData.PERSONAL_CYBER_CONTACT_DETAILS_FORM || personalCyberFormData.contactDetailsForm;

    if (!sumInsuredData?.selectedSumInsured) {
      return {
        success: false,
        error: 'Sum insured selection is required',
        data: null
      };
    }

    if (!contactDetailsData?.passengers || contactDetailsData.passengers.length === 0) {
      return {
        success: false,
        error: 'Customer information is required',
        data: null
      };
    }

    // Log customer data for debugging (let API handle validation)
    const customer = contactDetailsData.passengers[0];

    console.log('=== Personal Cyber Quote - Customer Data ===');
    console.log('Full Name:', customer.fullName);
    console.log('CPR Number:', customer.cprNumber);
    console.log('Email Address:', customer.emailAddress);
    console.log('Mobile Number:', customer.mobileNumber);
    console.log('Date of Birth Object:', customer.dateOfBirth);
    console.log('Full Customer Object:', customer);

    // Transform form data
    const requestPayload = transformPersonalCyberFormData(personalCyberFormData, partyCode);
    console.log('=== Personal Cyber Quote Request ===');
    console.log('Form data:', personalCyberFormData);
    console.log('Request payload:', requestPayload);
    console.log('Timeout:', timeout);

    const result = await makeAPICall(ENDPOINTS.CYBER_PERSONAL_QUOTE, requestPayload, {
      timeout: timeout
    });

    if (result.success) {
      const transformedData = transformPersonalCyberResponse(result.data);
      console.log('=== Personal Cyber Quote Success ===');
      console.log('Transformed data:', transformedData);

      return {
        success: true,
        data: transformedData,
        raw: result.data // Keep original for debugging
      };
    }

    console.error('=== Personal Cyber Quote Failed ===');
    console.error('Error:', result.error);
    console.error('Is timeout:', result.isTimeout);
    console.error('Is network error:', result.isNetworkError);

    return {
      success: false,
      error: result.error || 'Failed to get Personal Cyber quote',
      data: null,
      isNetworkError: result.isNetworkError,
      isServerError: result.isServerError,
      isTimeout: result.isTimeout
    };

  } catch (error) {
    console.error('Personal Cyber quote exception:', error);
    return {
      success: false,
      error: error.message || 'Failed to get Personal Cyber quote',
      data: null
    };
  }
};

/**
 * Transform smart device form data to API request format
 * @param {Object} smartDeviceFormData - Complete smart device form data from Redux
 * @param {string} partyCode - User's party code
 * @returns {Object} API request payload
 */
const transformSmartDeviceFormData = (smartDeviceFormData, partyCode = 'FALCON') => {
  const contactDetails = smartDeviceFormData.SMART_DEVICE_CONTACT_DETAILS_FORM || {};
  const deviceSelection = smartDeviceFormData.SMART_DEVICE_SELECTION_FORM || {};
  const purchaseDetails = smartDeviceFormData.SMART_DEVICE_PURCHASE_DETAILS_FORM || {};

  // Get customer data (first passenger is the customer)
  const customer = contactDetails.passengers?.[0] || contactDetails;

  // Helper function to format date to dd/mm/yyyy
  const formatDateToDDMMYYYY = (dateObj) => {
    if (!dateObj) return '';

    // Check if it's already a string in the right format
    if (typeof dateObj === 'string' && dateObj.includes('/')) {
      return dateObj;
    }

    // If it's an object with day, month, year properties
    if (dateObj.day && dateObj.month && dateObj.year) {
      const day = String(dateObj.day).padStart(2, '0');
      const month = String(dateObj.month).padStart(2, '0');
      const year = String(dateObj.year);
      return `${day}/${month}/${year}`;
    }

    // If it's a fullDate string (ISO format: yyyy-mm-dd)
    if (dateObj.fullDate) {
      const parts = dateObj.fullDate.split('-');
      if (parts.length === 3) {
        return `${parts[2]}/${parts[1]}/${parts[0]}`; // Convert yyyy-mm-dd to dd/mm/yyyy
      }
    }

    // If it's a Date object or date string
    try {
      const date = new Date(dateObj);
      if (!isNaN(date.getTime())) {
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = String(date.getFullYear());
        return `${day}/${month}/${year}`;
      }
    } catch (e) {
      console.error('Error parsing date:', e);
    }

    return '';
  };

  // Map deviceType to API format
  const deviceTypeMapping = {
    'smartphone': 'MP',    // Mobile Phone
    'tablet': 'TAB',       // Tablet
    'laptop': 'OTHERS',    // Others
    'smartwatch': 'OTHERS' // Others
  };

  const apiDeviceType = deviceTypeMapping[deviceSelection.deviceType?.toLowerCase()] || 'OTHERS';

  // Build the API payload
  const payload = {
    partyCode: partyCode,
    fullName: customer.fullName || '',
    sumInsured: parseFloat(purchaseDetails.devicePrice) || 0,
    brand: deviceSelection.brand || '',
    deviceType: apiDeviceType,
    imeiOrSerial: deviceSelection.imeiNumber || '',
    purchaseDateStr: formatDateToDDMMYYYY(purchaseDetails.purchaseDate),
    DateOfBirth: formatDateToDDMMYYYY(customer.dateOfBirth),
    cpr: customer.cprNumber || '',
    PhoneNumber: customer.mobileNumber || '',
    Email: customer.emailAddress || ''
  };

  // Once we have the customer's CPR, don't leak the FALCON guest fallback.
  // Exception: when buying for someone else, FALCON is intentional -- the insured person
  // is not in the system, so we must keep FALCON to avoid using the logged-in user's party code.
  const policyForValue = contactDetails.policyFor;
  if ((!partyCode || partyCode === 'FALCON') && payload.cpr && policyForValue !== 'someone_else') {
    delete payload.partyCode;
  }

  console.log('=== Smart Device Transform Debug ===');
  console.log('Contact Details:', contactDetails);
  console.log('Device Selection:', deviceSelection);
  console.log('Purchase Details:', purchaseDetails);
  console.log('Customer:', customer);
  console.log('Final Payload:', payload);

  return payload;
};

/**
 * Transform Smart Device API response to application format
 * @param {Object} apiResponse - Raw API response
 * @returns {Object} Transformed quote data
 */
const transformSmartDeviceResponse = (apiResponse) => {
  return {
    premium: apiResponse.Premium || 0,
    tax: apiResponse.Tax || 0,
    totalPremium: apiResponse.TotalPremium || 0,
    quoteNumber: apiResponse.PolicyNumber || '',
    policyNumber: apiResponse.PolicyNumber || '',
    planName: apiResponse.PlanName || 'Smart Device Insurance',
    productName: apiResponse.PlanName || 'Smart Device Insurance',
    // Include all original fields
    ...apiResponse
  };
};

/**
 * Get Smart Device Insurance quote from API
 * @param {Object} smartDeviceFormData - Complete smart device form data from Redux
 * @param {string} partyCode - User's party code (default: 'FALCON' for guest)
 * @param {Object} options - Additional options (timeout, etc.)
 * @returns {Promise<Object>} Quote response with success status
 */
export const getSmartDeviceQuote = async (smartDeviceFormData, partyCode = 'FALCON', options = {}) => {
  try {
    const { timeout = 30000 } = options;

    // Validate required data
    const deviceSelection = smartDeviceFormData.SMART_DEVICE_SELECTION_FORM;
    const purchaseDetails = smartDeviceFormData.SMART_DEVICE_PURCHASE_DETAILS_FORM;
    const contactDetails = smartDeviceFormData.SMART_DEVICE_CONTACT_DETAILS_FORM;

    if (!deviceSelection?.deviceType) {
      return {
        success: false,
        error: 'Device type is required',
        data: null
      };
    }

    if (!purchaseDetails?.devicePrice) {
      return {
        success: false,
        error: 'Device price is required',
        data: null
      };
    }

    if (!contactDetails?.passengers || contactDetails.passengers.length === 0) {
      return {
        success: false,
        error: 'Customer information is required',
        data: null
      };
    }

    // Transform form data to API format
    const requestPayload = transformSmartDeviceFormData(smartDeviceFormData, partyCode);

    console.log('=== Smart Device Quote Request ===');
    console.log('Form data:', smartDeviceFormData);
    console.log('Request payload:', requestPayload);
    console.log('Timeout:', timeout);

    const result = await makeAPICall(ENDPOINTS.SMART_DEVICE_QUOTE, requestPayload, {
      timeout: timeout
    });

    if (result.success) {
      const transformedData = transformSmartDeviceResponse(result.data);
      console.log('=== Smart Device Quote Success ===');
      console.log('Transformed data:', transformedData);

      return {
        success: true,
        data: transformedData,
        raw: result.data // Keep original for debugging
      };
    }

    console.error('=== Smart Device Quote Failed ===');
    console.error('Error:', result.error);
    console.error('Is timeout:', result.isTimeout);
    console.error('Is network error:', result.isNetworkError);

    return {
      success: false,
      error: result.error || 'Failed to get Smart Device quote',
      data: null,
      isNetworkError: result.isNetworkError,
      isServerError: result.isServerError,
      isTimeout: result.isTimeout
    };

  } catch (error) {
    console.error('Smart Device quote exception:', error);
    return {
      success: false,
      error: error.message || 'Failed to get Smart Device quote',
      data: null
    };
  }
};

/**
 * Add a family member to an existing Travel Insurance policy
 * This endpoint is ONLY for Family Travel policies
 */
export const addTravelFamilyMember = async (memberData, token, options = {}) => {
  try {
    const { timeout = 30000 } = options;
    const requiredFields = ["PolicyNumber", "FullName", "DateOfBirth", "CPR", "PhoneNumber", "Relationship", "Gender"];
    for (const field of requiredFields) {
      if (!memberData[field]) {
        return { success: false, error: `${field} is required`, data: null };
      }
    }
    const validRelationships = ["SPOUSE", "CHILD"];
    if (!validRelationships.includes(memberData.Relationship)) {
      return { success: false, error: "Relationship must be SPOUSE or CHILD", data: null };
    }
    const validGenders = ["M", "F"];
    if (!validGenders.includes(memberData.Gender)) {
      return { success: false, error: "Gender must be M or F", data: null };
    }
    console.log("=== Add Travel Family Member Request ===", memberData);
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);
    try {
      const headers = { "Content-Type": "application/json", "Authorization": `Bearer ${token}` };
      const startTime = Date.now();
      const response = await fetch(`${BASE_URL}travel/add-member`, {
        method: "POST", headers: headers, body: JSON.stringify(memberData), signal: controller.signal,
      });
      clearTimeout(timeoutId);
      console.log(`Add member API call completed in ${Date.now() - startTime}ms, status: ${response.status}`);
      if (!response.ok) {
        let errorMessage = `API Error: ${response.status} ${response.statusText}`;
        try { const errorData = await response.json(); errorMessage = errorData.ErrorMessageEn || errorData.message || errorMessage; } catch (e) {}
        return { success: false, error: errorMessage, data: null };
      }
      const result = await response.json();
      console.log("=== Add Member Success ===", result);
      return { success: true, data: result };
    } catch (fetchError) {
      clearTimeout(timeoutId);
      if (fetchError.name === "AbortError") {
        return { success: false, error: `Request timed out after ${timeout}ms`, isTimeout: true };
      }
      throw fetchError;
    }
  } catch (error) {
    console.error("Add family member exception:", error);
    return { success: false, error: error.message || "Failed to add family member", data: null };
  }
};

/**
 * Get travel plan prices for all plans using new /travel/plan-prices endpoint
 * @param {Object} formData - Travel form data including dates and plan type
 * @param {Object} options - Additional options like timeout
 * @returns {Promise<Object>} Plan prices response
 */
export const getTravelPlanPrices = async (formData, options = {}) => {
  try {
    const { timeout = 30000 } = options;

    // Validate required data
    if (!formData.departureDate || !formData.returnDate || !formData.planType) {
      return {
        success: false,
        error: 'Missing required travel information (dates, planType)',
        data: null
      };
    }

    // Convert dates to Unix timestamps
    const fromDate = convertToUnixTimestamp(formData.departureDate);
    const toDate = convertToUnixTimestamp(formData.returnDate);

    if (!fromDate || !toDate) {
      return {
        success: false,
        error: 'Invalid date format',
        data: null
      };
    }

    // Prepare payload based on plan type
    let requestPayload;
    
    if (formData.planType === 'individual') {
      requestPayload = {
        fromDate: fromDate,
        toDate: toDate,
        policyType: 'INDIVIDUAL',
        isAnnual: false
      };
    } else if (formData.planType === 'family') {
      // Count family members from passenger data
      const familyMembersCount = formData.passengers?.length || 2; // Default to 2 for family
      
      requestPayload = {
        fromDate: fromDate,
        toDate: toDate,
        policyType: 'FAMILY',
        members: familyMembersCount
      };
    } else {
      return {
        success: false,
        error: 'Invalid plan type. Must be "individual" or "family"',
        data: null
      };
    }

    console.log('=== Travel Plan Prices Request ===');
    console.log('Form data:', formData);
    console.log('Request payload:', requestPayload);
    console.log('Timeout:', timeout);

    const result = await makeAPICall(ENDPOINTS.TRAVEL_PLAN_PRICES, requestPayload, {
      timeout: timeout
    });

    if (result.success) {
      console.log('=== Travel Plan Prices Success ===');
      console.log('Response data:', result.data);

      return {
        success: true,
        data: result.data,
        raw: result.data // Keep original for debugging
      };
    }

    console.error('=== Travel Plan Prices Failed ===');
    console.error('Error:', result.error);
    console.error('Is timeout:', result.isTimeout);
    console.error('Is network error:', result.isNetworkError);

    return {
      success: false,
      error: result.error || 'Failed to get travel plan prices',
      data: null,
      isNetworkError: result.isNetworkError,
      isServerError: result.isServerError,
      isTimeout: result.isTimeout
    };

  } catch (error) {
    console.error('Travel plan prices exception:', error);
    return {
      success: false,
      error: error.message || 'Failed to get travel plan prices',
      data: null
    };
  }
};

/**
 * Get travel cover prices using /travel/cover-prices endpoint
 * @param {Object} formData - Travel form data including plan details
 * @param {Object} options - Additional options like timeout
 * @returns {Promise<Object>} Cover prices response
 */
export const getTravelCoverPrices = async (formData, options = {}) => {
  try {
    const { timeout = 15000 } = options; // Shorter timeout for cover prices

    // Validate required data
    if (!formData || !formData.selectedPlan || !formData.departureDate || !formData.returnDate || !formData.planType) {
      return {
        success: false,
        error: 'Missing required data for cover prices (selectedPlan, dates, planType)',
        data: null,
        covers: []
      };
    }

    // Map plan codes to match API expectations
    const planCodeMapping = {
      'silver': 'silver',    // Silver plan
      'platinum': 'PL',      // Platinum plan  
      'gold': 'GO'           // Gold plan
    };

    const planCode = planCodeMapping[formData.selectedPlan?.toLowerCase()] || 'silver';
    
    // Convert dates to Unix timestamps
    const fromDate = convertToUnixTimestamp(formData.departureDate);
    const toDate = convertToUnixTimestamp(formData.returnDate);

    if (!fromDate || !toDate) {
      return {
        success: false,
        error: 'Invalid date format for cover prices',
        data: null,
        covers: []
      };
    }

    // Prepare payload according to API specification
    const requestPayload = {
      planType: formData.planType.toUpperCase(), // INDIVIDUAL or FAMILY
      planCode: planCode,
      fromDate: fromDate,
      toDate: toDate,
      isAnnual: false // Always false for now as specified
    };

    console.log('=== Travel Cover Prices Request ===');
    console.log('Form data:', formData);
    console.log('Request payload:', requestPayload);
    console.log('Timeout:', timeout);

    const result = await makeAPICall(ENDPOINTS.TRAVEL_COVER_PRICES, requestPayload, {
      timeout: timeout
    });

    if (result.success) {
      console.log('=== Travel Cover Prices Success ===');
      console.log('Response data:', result.data);

      // Transform covers response to expected format
      const covers = result.data.Covers || [];
      console.log('Available covers:', covers.length);
      covers.forEach(cover => {
        console.log(`- ${cover.CoverName}: ${cover.Price} BHD (Code: ${cover.CoverCode})`);
      });

      return {
        success: true,
        data: result.data,
        covers: covers,
        raw: result.data // Keep original for debugging
      };
    }

    console.error('=== Travel Cover Prices Failed ===');
    console.error('Error:', result.error);
    console.error('Is timeout:', result.isTimeout);
    console.error('Is network error:', result.isNetworkError);

    return {
      success: false,
      error: result.error || 'Failed to get travel cover prices',
      data: null,
      covers: [],
      isNetworkError: result.isNetworkError,
      isServerError: result.isServerError,
      isTimeout: result.isTimeout
    };

  } catch (error) {
    console.error('Travel cover prices exception:', error);
    return {
      success: false,
      error: error.message || 'Failed to get travel cover prices',
      data: null,
      covers: []
    };
  }
};

export const getBniTravelCoverPrices = async (formData, options = {}) => {
  try {
    const { timeout = 15000 } = options;

    if (!formData || !formData.selectedPlan || !formData.departureDate || !formData.returnDate || !formData.planType) {
      return {
        success: false,
        error: 'Missing required data for BNI cover prices (selectedPlan, dates, planType)',
        data: null,
        covers: []
      };
    }

    const bniPlanCodeMapping = {
      'schengen': 'SCHENGENB',
      'worldwide_ex': 'WLEXB',
      'worldwide': 'WLB',
      'schengenb': 'SCHENGENB',
      'wlexb': 'WLEXB',
      'wlb': 'WLB',
    };

    const planCode = bniPlanCodeMapping[formData.selectedPlan?.toLowerCase()];
    if (!planCode) {
      return {
        success: false,
        error: `Invalid BNI plan code: ${formData.selectedPlan}. Valid options are: schengen, worldwide_ex, worldwide.`,
        data: null,
        covers: []
      };
    }

    const fromDate = convertToUnixTimestamp(formData.departureDate);
    const toDate = convertToUnixTimestamp(formData.returnDate);

    if (!fromDate || !toDate) {
      return {
        success: false,
        error: 'Invalid date format for BNI cover prices',
        data: null,
        covers: []
      };
    }

    const requestPayload = {
      planType: formData.planType.toUpperCase(),
      planCode: planCode,
      fromDate: fromDate,
      toDate: toDate,
      isAnnual: Boolean(formData.isAnnual)
    };

    console.log('=== BNI Travel Cover Prices Request ===');
    console.log('Form data:', formData);
    console.log('Request payload:', requestPayload);

    const result = await makeAPICall(ENDPOINTS.BNI_TRAVEL_COVER_PRICES, requestPayload, {
      timeout: timeout
    });

    if (result.success) {
      console.log('=== BNI Travel Cover Prices Success ===');
      const covers = result.data.Covers || [];
      covers.forEach(cover => {
        console.log(`- ${cover.CoverName}: ${cover.Price} BHD (Code: ${cover.CoverCode})`);
      });

      return {
        success: true,
        data: result.data,
        covers: covers,
        raw: result.data
      };
    }

    console.error('=== BNI Travel Cover Prices Failed ===');
    return {
      success: false,
      error: result.error || 'Failed to get BNI travel cover prices',
      data: null,
      covers: [],
      isNetworkError: result.isNetworkError,
      isServerError: result.isServerError,
      isTimeout: result.isTimeout
    };

  } catch (error) {
    console.error('BNI travel cover prices exception:', error);
    return {
      success: false,
      error: error.message || 'Failed to get BNI travel cover prices',
      data: null,
      covers: []
    };
  }
};

/**
 * Get home cover prices using /home/cover-prices endpoint
 * @param {Object} homeFormData - Home form data including building and contents values
 * @param {Object} options - Additional options like timeout
 * @returns {Promise<Object>} Cover prices response
 */
export const getHomeCoverPrices = async (homeFormData, options = {}) => {
  try {
    const { timeout = 15000 } = options; // Shorter timeout for cover prices

    // Validate required data
    if (!homeFormData || !homeFormData.homeDetails) {
      return {
        success: false,
        error: 'Missing required home data for cover prices',
        data: null,
        covers: []
      };
    }

    const homeDetails = homeFormData.homeDetails;

    // Helper function to clean and parse numeric values
    const parseCleanFloat = (value) => {
      if (!value || value === "") return 0;
      return parseFloat(String(value).replace(/[,\s]/g, "")) || 0;
    };

    // Calculate values from home details
    const buildingValue = parseCleanFloat(homeDetails.buildingValue);
    // Content Details section is always visible now, so always include these values
    const contentsValue = parseCleanFloat(homeDetails.contentsValue);
    const personalBelongingsValue = parseCleanFloat(homeDetails.personalBelongingsValue);
    const calculatedSum = buildingValue + contentsValue + personalBelongingsValue;
    // In the renewal flow the component breakdown may all be zero while the policy
    // SumInsured is non-zero; use the explicit sumInsured field as a fallback so the
    // cover prices API always receives the correct total.
    const sumInsured = calculatedSum || parseCleanFloat(homeDetails.sumInsured);

    // Prepare payload according to API specification
    const requestPayload = {
      SumInsured: sumInsured,
      FixValue: buildingValue,
      PersonalValue: personalBelongingsValue,
      ContentValue: contentsValue
    };

    console.log('=== Home Cover Prices Request ===');
    console.log('Home form data:', homeFormData);
    console.log('Request payload:', requestPayload);
    console.log('Timeout:', timeout);

    const result = await makeAPICall(ENDPOINTS.HOME_COVER_PRICES, requestPayload, {
      timeout: timeout
    });

    if (result.success) {
      console.log('=== Home Cover Prices Success ===');
      console.log('Response data:', result.data);

      // Transform covers response to expected format
      const covers = result.data.Covers || [];
      console.log('Available home covers:', covers.length);
      covers.forEach(cover => {
        console.log(`- ${cover.CoverName}: ${cover.Price} BHD (Code: ${cover.CoverCode})`);
      });

      return {
        success: true,
        data: result.data,
        covers: covers,
        raw: result.data // Keep original for debugging
      };
    }

    console.error('=== Home Cover Prices Failed ===');
    console.error('Error:', result.error);
    console.error('Is timeout:', result.isTimeout);
    console.error('Is network error:', result.isNetworkError);

    return {
      success: false,
      error: result.error || 'Failed to get home cover prices',
      data: null,
      covers: [],
      isNetworkError: result.isNetworkError,
      isServerError: result.isServerError,
      isTimeout: result.isTimeout
    };

  } catch (error) {
    console.error('Home cover prices exception:', error);
    return {
      success: false,
      error: error.message || 'Failed to get home cover prices',
      data: null,
      covers: []
    };
  }
};

/**
 * Get travel policy members
 * @param {string} policyNumber - Policy number
 * @returns {Promise<Object>} Members response
 */
export const getTravelMembers = async (policyNumber) => {
  try {
    console.log('=== Getting Travel Members (POST) ===');
    console.log('Policy Number:', policyNumber);

    const payload = {
      PolicyNumber: policyNumber
    };

    const result = await makeAuthenticatedAPICall(ENDPOINTS.TRAVEL_MEMBERS, payload, {
      timeout: 30000
    });

    if (result.success) {
      console.log('=== Travel Members Success ===');
      console.log('Members data:', result.data);

      return {
        success: true,
        data: result.data
      };
    }

    console.error('=== Travel Members Failed ===');
    console.error('Error:', result.error);

    return {
      success: false,
      error: result.error || 'Failed to get travel members',
      data: null
    };

  } catch (error) {
    console.error('Travel members exception:', error);
    return {
      success: false,
      error: error.message || 'Failed to get travel members',
      data: null
    };
  }
};

/**
 * Update travel policy members
 * @param {Object} updateData - Update data containing PolicyNumber and Members array
 * @returns {Promise<Object>} Update response
 */
export const updateTravelMembers = async (updateData) => {
  try {
    console.log('=== Updating Travel Members ===');
    console.log('Update data:', updateData);

    const result = await makeAuthenticatedAPICall(ENDPOINTS.TRAVEL_UPDATE_MEMBERS, updateData, {
      timeout: 30000
    });

    if (result.success) {
      console.log('=== Travel Members Update Success ===');
      console.log('Update response:', result.data);

      return {
        success: true,
        data: result.data
      };
    }

    console.error('=== Travel Members Update Failed ===');
    console.error('Error:', result.error);

    return {
      success: false,
      error: result.error || 'Failed to update travel members',
      data: null
    };

  } catch (error) {
    console.error('Travel members update exception:', error);
    return {
      success: false,
      error: error.message || 'Failed to update travel members',
      data: null
    };
  }
};

/**
 * Delete a travel member/passenger from a policy
 * @param {string} policyNumber - The policy number
 * @param {string} memberCode - The member code to delete
 * @returns {Promise<{success: boolean, data: any, error: string|null}>}
 */
export const deleteTravelMember = async (policyNumber, memberCode) => {
  try {
    console.log('=== Deleting Travel Member ===');
    console.log('Policy Number:', policyNumber);
    console.log('Member Code:', memberCode);

    if (!policyNumber || !memberCode) {
      return {
        success: false,
        error: 'Policy number and member code are required',
        data: null
      };
    }

    const payload = {
      PolicyNumber: policyNumber,
      MemberCode: memberCode
    };

    const result = await makeAuthenticatedAPICall(ENDPOINTS.TRAVEL_DELETE_MEMBER, payload, {
      timeout: 30000
    });

    if (result.success) {
      console.log('=== Travel Member Delete Success ===');
      console.log('Delete response:', result.data);

      return {
        success: true,
        data: result.data
      };
    }

    console.error('=== Travel Member Delete Failed ===');
    console.error('Error:', result.error);

    return {
      success: false,
      error: result.error || 'Failed to delete travel member',
      data: null
    };

  } catch (error) {
    console.error('Travel member delete exception:', error);
    return {
      success: false,
      error: error.message || 'Failed to delete travel member',
      data: null
    };
  }
};

// Generic authenticated API call function
const makeAuthenticatedAPICall = async (endpoint, data, options = {}) => {
  const { timeout = 30000, requireAuth = true } = options;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    console.log(`=== Making Authenticated API call ===`);
    console.log(`Endpoint: ${BASE_URL}${endpoint}`);
    console.log(`Payload:`, JSON.stringify(data, null, 2));

    // Get auth token from localStorage using the same method as axios interceptor
    const LOCAL_STORAGE_NAME = "CUSTOMER_PORTAL";
    const userInfo = JSON.parse(localStorage.getItem(LOCAL_STORAGE_NAME) || '{}');
    const token = userInfo?.access_token;

    if (requireAuth && !token) {
      throw new Error('No authentication token available');
    }

    console.log('🔑 Using auth token:', token ? 'Token found' : 'No token (guest)');

    const headers = {
      'Content-Type': 'application/json',
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const startTime = Date.now();
    const response = await fetch(`${BASE_URL}${endpoint}`, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify(data),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);
    const duration = Date.now() - startTime;

    console.log(`API Response - Status: ${response.status}, Duration: ${duration}ms`);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('API Error Response:', errorText);
      
      return {
        success: false,
        error: `HTTP ${response.status}: ${errorText}`,
        data: null,
        isServerError: response.status >= 500,
        isNetworkError: false,
        isTimeout: false
      };
    }

    const result = await response.json();
    console.log('API Success Response:', result);

    return {
      success: true,
      data: result,
      error: null
    };

  } catch (error) {
    clearTimeout(timeoutId);
    
    let errorMessage = error.message || 'Unknown error';
    let isTimeout = false;
    let isNetworkError = false;

    if (error.name === 'AbortError') {
      errorMessage = 'Request timeout';
      isTimeout = true;
    } else if (error instanceof TypeError && error.message.includes('fetch')) {
      errorMessage = 'Network connection error';
      isNetworkError = true;
    }

    console.error(`API Error - ${errorMessage}:`, error);

    return {
      success: false,
      error: errorMessage,
      data: null,
      isTimeout,
      isNetworkError,
      isServerError: false
    };
  }
};

/**
 * Apply promo code to a quotation
 * Requires authentication (Bearer token)
 * @param {string} quoteNumber - Quote number from quote API response (PolicyNumber field)
 * @param {string} promoCode - Promo code to apply
 * @param {Object} options - Optional parameters (timeout, etc.)
 * @returns {Promise} API response with updated quote including promo discount
 */
export const applyPromoCode = async (quoteNumber, promoCode, options = {}) => {
  try {
    const { timeout = 30000 } = options;

    // Validate required parameters
    if (!quoteNumber || !promoCode) {
      return {
        success: false,
        error: 'Quote number and promo code are required',
        data: null
      };
    }

    console.log('=== Apply Promo Code Request ===');
    console.log('Quote Number:', quoteNumber);
    console.log('Promo Code:', promoCode);
    console.log('Channel:', getClientSource());

    // Prepare request payload
    const requestPayload = {
      QuoteNumber: quoteNumber,
      Channel: getClientSource(),
      PromoCode: promoCode
    };

    console.log('Request Payload:', JSON.stringify(requestPayload, null, 2));

    // Make API call — promo endpoint accepts both authenticated and guest users,
    // so don't require a token (will still attach one if present in localStorage).
    const result = await makeAuthenticatedAPICall(
      ENDPOINTS.APPLY_PROMO,
      requestPayload,
      { timeout, requireAuth: false }
    );

    if (result.success) {
      console.log('=== Apply Promo Code Success ===');
      console.log('Updated Quote:', result.data);
      console.log('New Policy Number:', result.data?.PolicyNumber);
      console.log('Total Premium:', result.data?.TotalPremium);
      console.log('Premium:', result.data?.Premium);
      console.log('Tax:', result.data?.Tax);

      // Check if promo discount is present in covers
      const promoDiscount = result.data?.QuoteDetails?.Covers?.find(cover =>
        cover.coverProperty?.simpleProperty?.some(prop =>
          prop.paramName === 'Promo Code Discount'
        )
      );

      if (promoDiscount) {
        const discountValue = promoDiscount.coverProperty.simpleProperty.find(
          prop => prop.paramName === 'Promo Code Discount'
        )?.paramValue;
        console.log('✅ Promo Code Applied! Discount:', discountValue);
      }

      return {
        success: true,
        data: result.data,
        error: null
      };
    }

    console.error('=== Apply Promo Code Failed ===');
    console.error('Error:', result.error);

    return {
      success: false,
      error: result.error || 'Failed to apply promo code',
      data: null,
      isServerError: result.isServerError,
      isNetworkError: result.isNetworkError,
      isTimeout: result.isTimeout
    };

  } catch (error) {
    console.error('Apply promo code exception:', error);
    return {
      success: false,
      error: error.message || 'Failed to apply promo code',
      data: null
    };
  }
};

export default {
  checkPartyCode,
  getTravelQuote,
  getTravelQuoteOptimized,
  getBniTravelQuote,
  getBniTravelQuoteOptimized,
  getBniTravelPlanPrices,
  clearTravelQuoteCache,
  getHomeQuote,
  getPersonalCyberQuote,
  getSmartDeviceQuote,
  addTravelFamilyMember,
  getTravelPlanPrices,
  getTravelCoverPrices,
  getTravelMembers,
  updateTravelMembers,
  applyPromoCode
};